import { requestLaravel } from "../../../utils/api";
import { GlobalLogPayload } from "../../../utils/GlobalLogging";
import { WeatherAndLocationData } from "../../PublicPages/core/models";
import {
  ApiResponse,
  ICategory,
  IDashboardFilterData,
  IDepartment,
  IDistrict,
  ILocation,
  ILookup,
  ILookupAPIResp,
  IMunicipality,
  INotificationDataResponse,
  INotificationItem,
  IProvince,
  ISector,
  IUser,
  PipelineDepartmentData,
  PipelineOverviewData,
  PipelineSectorData,
} from "./_models";

export function getSectorSubSectors(signal?: AbortSignal) {
  return requestLaravel<ApiResponse<{ sectors: ISector[] }>>(
    "/sectors/get-sectors-and-sub-sectors",
    {},
    { signal },
  );
}

export function getSectors(signal?: AbortSignal): Promise<ApiResponse<ISector[]>> {
  return requestLaravel<ApiResponse<ISector[]>>("/sectors/get-sectors", {}, { signal });
}

export function getLocations(signal?: AbortSignal) {
  return requestLaravel<ApiResponse<{ locations: ILocation[] }>>(
    "/locations/get-all-locations",
    {},
    { signal },
  );
}

export function getDepartments(signal?: AbortSignal) {
  return requestLaravel<ApiResponse<{ departments: IDepartment[] }>>(
    "/departments/get-all-departments",
    {},
    { signal },
  );
}

export function getDistrictMunicipality(signal?: AbortSignal) {
  return requestLaravel<ApiResponse<IDistrict[]>>(
    "/locations/get-all-districts",
    {},
    { signal },
  );
}

export function getLocalDistrictMunicipality(
  district_id: number,
  signal?: AbortSignal,
) {
  return requestLaravel<ApiResponse<IMunicipality[]>>(
    "/locations/get-municipalities-by-district",
    {
      district_id,
    },
    { signal },
  );
}

// lookup_name : project_priority,project_status,project_score,project_stage,stage_status,project_objectives,project_focus_area,user_designation
export function getLookupDetails(
  lookup_name: string,
  project_id: string = "",
  signal?: AbortSignal,
) {
  const data = {
    lookup_name: lookup_name,
    project_id: project_id,
  };
  return requestLaravel<ApiResponse<ILookupAPIResp>>(
    "/lookups/get-lookup-details",
    data,
    { signal },
  );
}

// dev_names limits the list to those categories (e.g. ["moa", "mou", "sla"]); all categories when left out.
export function getCategoryList(dev_names?: string[]) {
  return requestLaravel<ApiResponse<{ list: ICategory[] }>>(
    "/document-categories/get-category-list",
    dev_names?.length ? { dev_name: dev_names } : undefined,
  );
}

interface InsertObjectivesResponseData extends ILookup {
  row_id: number;
}

export function insertObjectives(insertData: {
  project_id: string | number;
  data_key: string;
  new_datakey_name: string;
  user_id?: string | number;
}) {
  const { project_id, data_key, new_datakey_name } = insertData;
  return requestLaravel<ApiResponse<{ list: InsertObjectivesResponseData }>>(
    "/lookups/add-objectives-in-lookup-details",
    {
      project_id,
      data_key,
      new_datakey_name,
    },
  );
}

export function getEvaluatorUserList(
  signal?: AbortSignal,
): Promise<ApiResponse<{ list: IUser[] }>> {
  return requestLaravel<ApiResponse<{ list: IUser[] }>>(
    "/users/get-evaluator-user-list",
    {},
    { signal },
  );
}

export function getWebPageData(
  page_name: string,
  page_version?: string,
): Promise<ApiResponse<{ page_version: string; webpage_data: unknown }>> {
  return requestLaravel<
    ApiResponse<{ page_version: string; webpage_data: unknown }>
  >("/web-pages/get-web-page-data", {
    page_name,
    ...(page_version !== undefined ? { page_version } : {}),
  });
}

interface PipelineItem {
  title: string;
  desc: string;
  number: number;
}

interface PipelineData {
  existing_pipeline: PipelineItem;
  prioritised_pipeline: PipelineItem;
  project_evaluation_prepacking: PipelineItem;
  pipeline_portfolio: PipelineItem;
}

interface PipelineDashboardStatusData {
  four_box: PipelineData;
  pipeline_overview_graph: PipelineOverviewData[];
  pipeline_sector_graph: PipelineSectorData;
  pipeline_department_graph?: PipelineDepartmentData;
}

export function getPipelineDashboardStatusData(
  filterData: IDashboardFilterData,
  signal?: AbortSignal,
) {
  const data = {
    ...filterData,
  };
  return requestLaravel<ApiResponse<PipelineDashboardStatusData>>(
    "/dashboard/get-pipeline-dashboard-status-data",
    data,
    { signal },
  );
}

export function exportPipelineStatusPdf(filterData: IDashboardFilterData) {
  return requestLaravel<ApiResponse<{ url: string; file_name: string; file_type: string; export_type: string }>>(
    "/dashboard/export-pipeline-status-pdf",
    { ...filterData },
  );
}

export function getConfigDatafromAPI(
  signal?: AbortSignal,
): Promise<ApiResponse<{ config: Record<string, string> }>> {
  return requestLaravel<ApiResponse<{ config: Record<string, string> }>>(
    "/platform-config/get-platform-config",
    {},
    { signal },
  );
}

export function getConfigDataByKeyfromAPI(
  key: string,
): Promise<ApiResponse<{ config: string }>> {
  return requestLaravel<ApiResponse<{ config: string }>>(
    "/platform-config/get-platform-config-by-key",
    { key },
  );
}

export function getNotifications(
  payload: {
    only_unread?: 0 | 1;
    limit?: number;
    offset?: number;
    search?: string;
    notification_type?: string;
    is_starred?: 0 | 1;
  } = {},
  signal?: AbortSignal,
): Promise<ApiResponse<INotificationDataResponse>> {
  return requestLaravel<ApiResponse<INotificationDataResponse>>(
    "/notifications/list",
    payload,
    { signal },
  );
}

export function getUnreadNotifications(
  payload: {
    limit?: number;
    offset?: number;
    search?: string;
  } = {},
): Promise<ApiResponse<INotificationDataResponse>> {
  return requestLaravel<ApiResponse<INotificationDataResponse>>(
    "/notifications/unread/list",
    payload,
  );
}

export function updateNotificationFlags(payload: {
  ids: number[];
  [key: string]: any;
}): Promise<ApiResponse> {
  return requestLaravel<ApiResponse>("/notifications/flags/update", payload);
}

export function deleteNotificationFlags(payload: {
  ids: number[];
  delete_all?: number;
}): Promise<ApiResponse> {
  return requestLaravel<ApiResponse>("/notifications/delete", payload);
}

export function acknowledgeNotification(payload: {
  id: number;
}): Promise<ApiResponse<INotificationItem>> {
  return requestLaravel<ApiResponse<INotificationItem>>(
    "/notifications/acknowledge",
    payload,
  );
}

export function Logout(user_id_token: string): Promise<ApiResponse> {
  return requestLaravel("/logout", { user_id_token: user_id_token });
}

export function getWeatherData(
  payload: any,
  signal?: AbortSignal,
): Promise<ApiResponse<WeatherAndLocationData>> {
  const data = {
    ...payload,
  };
  return requestLaravel<ApiResponse<WeatherAndLocationData>>(
    "/weather",
    data,
    { signal },
  );
}

export function getProvinces(): Promise<ApiResponse<IProvince[]>> {
  return requestLaravel<ApiResponse<IProvince[]>>(
    "/locations/get-provinces",
    {},
  );
}

export function HandleGlobalLoggingApi(
  logData?: GlobalLogPayload,
): Promise<ApiResponse> {
  return requestLaravel<ApiResponse>("/platform-logs/write-platform-log", {
    ...(logData ?? {}),
  });
}
